import { create } from 'zustand';

import type { ThemeColorKey, ThemeColors, ThemeConfig, ThemeLogoKey } from '@/infra/theme/theme-config.js';
import { DEFAULT_THEME } from '@/infra/theme/theme-defaults.js';

const STORAGE_KEY = 'app-theme';

type ThemePersistence = {
    theme: ThemeConfig;
    hasClientOverride: boolean;
};

function cloneDefaultTheme(): ThemeConfig {
    return {
        ...DEFAULT_THEME,
        colors: { ...DEFAULT_THEME.colors },
        logos: { ...DEFAULT_THEME.logos, header: '' },
        typography: { ...DEFAULT_THEME.typography },
        layout: { ...DEFAULT_THEME.layout }
    };
}

function isThemePersistence(value: unknown): value is ThemePersistence {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>;
    return typeof record.hasClientOverride === 'boolean' && typeof record.theme === 'object' && record.theme !== null;
}

function parsePersistedTheme(raw: string): ThemePersistence | null {
    const parsed: unknown = JSON.parse(raw);
    if (isThemePersistence(parsed)) {
        return {
            theme: { ...DEFAULT_THEME, ...parsed.theme },
            hasClientOverride: parsed.hasClientOverride
        };
    }
    if (typeof parsed === 'object' && parsed !== null && 'colors' in parsed) {
        return {
            theme: { ...DEFAULT_THEME, ...(parsed as ThemeConfig) },
            hasClientOverride: false
        };
    }
    return null;
}

function readPersistedState(): ThemePersistence {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return { theme: cloneDefaultTheme(), hasClientOverride: false };
        }
        return parsePersistedTheme(stored) ?? { theme: cloneDefaultTheme(), hasClientOverride: false };
    } catch {
        return { theme: cloneDefaultTheme(), hasClientOverride: false };
    }
}

function persistTheme(theme: ThemeConfig, hasClientOverride: boolean): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme, hasClientOverride }));
}

interface ThemeStore {
    theme: ThemeConfig;
    defaultColors: ThemeColors;
    hasClientOverride: boolean;
    isLoading: boolean;
    isDirty: boolean;
    loadTheme: () => Promise<void>;
    saveTheme: () => Promise<void>;
    resetTheme: () => void;
    resetToDefaultTheme: () => void;
    applyClientTheme: (colors: ThemeColors | null, logoUrl: string | null) => void;
    setDefaultColors: (colors: ThemeColors) => void;
    updateColor: (key: ThemeColorKey, value: string) => void;
    updateLogo: (key: ThemeLogoKey, value: string) => void;
    updateTypography: (key: 'fontFamily' | 'headingFontFamily' | 'fontUrl' | 'baseFontSize', value: string) => void;
    updateLayout: (key: 'borderRadius' | 'headerHeight' | 'sidebarWidth', value: string) => void;
    setDarkMode: (mode: 'light' | 'dark' | 'system') => void;
    setCustomCss: (css: string) => void;
    applyPreset: (colors: ThemeConfig['colors']) => void;
}

const persisted = readPersistedState();

export const useThemeStore = create<ThemeStore>((set, get) => ({
    theme: persisted.theme,
    defaultColors: { ...DEFAULT_THEME.colors },
    hasClientOverride: persisted.hasClientOverride,
    isLoading: false,
    isDirty: false,

    loadTheme: async () => {
        set({ isLoading: true });
        try {
            const next = readPersistedState();
            set({ theme: next.theme, hasClientOverride: next.hasClientOverride, isDirty: false });
        } finally {
            set({ isLoading: false });
        }
    },

    saveTheme: async () => {
        persistTheme(get().theme, get().hasClientOverride);
        set({ isDirty: false });
    },

    resetTheme: () => set({ theme: cloneDefaultTheme(), isDirty: true }),

    resetToDefaultTheme: () => {
        const nextTheme = cloneDefaultTheme();
        nextTheme.colors = { ...get().defaultColors };
        persistTheme(nextTheme, false);
        set({ theme: nextTheme, hasClientOverride: false, isDirty: false });
    },

    applyClientTheme: (colors, logoUrl) => {
        if (!colors && !logoUrl) {
            get().resetToDefaultTheme();
            return;
        }
        const current = get().theme;
        const nextTheme: ThemeConfig = {
            ...current,
            colors: colors ? { ...colors } : { ...get().defaultColors },
            logos: { ...current.logos, header: logoUrl ?? '' }
        };
        persistTheme(nextTheme, true);
        set({ theme: nextTheme, hasClientOverride: true, isDirty: false });
    },

    setDefaultColors: (colors) => {
        const nextDefault = { ...colors };
        if (!get().hasClientOverride) {
            const nextTheme = { ...get().theme, colors: { ...nextDefault } };
            persistTheme(nextTheme, false);
            set({ defaultColors: nextDefault, theme: nextTheme, isDirty: false });
            return;
        }
        set({ defaultColors: nextDefault });
    },

    updateColor: (key, value) => set((s) => ({ theme: { ...s.theme, colors: { ...s.theme.colors, [key]: value } }, isDirty: true })),
    updateLogo: (key, value) => set((s) => ({ theme: { ...s.theme, logos: { ...s.theme.logos, [key]: value } }, isDirty: true })),
    updateTypography: (key, value) => set((s) => ({ theme: { ...s.theme, typography: { ...s.theme.typography, [key]: value } }, isDirty: true })),
    updateLayout: (key, value) => set((s) => ({ theme: { ...s.theme, layout: { ...s.theme.layout, [key]: value } }, isDirty: true })),
    setDarkMode: (mode) => {
        const nextTheme = { ...get().theme, darkMode: mode };
        persistTheme(nextTheme, get().hasClientOverride);
        set({ theme: nextTheme, isDirty: false });
    },
    setCustomCss: (css) => set((s) => ({ theme: { ...s.theme, customCss: css }, isDirty: true })),
    applyPreset: (colors) => set((s) => ({ theme: { ...s.theme, colors: { ...colors } }, isDirty: true }))
}));
